import { mutator } from 'satcheljs';
import {
    moveTimeStreamAsset,
    updateAssetSize,
    patchAssetPayload,
    resetIsNewlyCreated,
    setColor,
    setDueDate,
    setIsComplete,
    setTaskId,
    trackAssetSize,
    removeBucketChild,
    updateAssetParent,
    moveTimeStreamAssetToPosition,
} from '../actions';
import { upsertClientAssets } from '../privateActions';
import getAsset from '../selectors/getAsset';
import getAssetOrUndefined from '../selectors/getAssetOrUndefined';
import getLocalAssetId from '../assetId/getLocalAssetId';
import getProject from '../selectors/getProject';
import upsertAssets from './assets/upsertAssets';
import type { AssetId, ProjectId } from 'owa-timestream-types';

mutator(moveTimeStreamAsset, ({ projectId, assetId, x, y }) => {
    moveAsset(projectId, assetId, x, y);
});

mutator(moveTimeStreamAssetToPosition, ({ projectId, assetId, x, y }) => {
    moveAsset(projectId, assetId, x, y);
});

mutator(updateAssetSize, ({ projectId, assetId, width, height, x, y }) => {
    const asset = getAsset(projectId, assetId)!;

    asset.bounds.width = width;
    asset.bounds.height = height;
    asset.bounds.x = x;
    asset.bounds.y = y;
});

mutator(updateAssetParent, ({ projectId, assetId, x, y }) => {
    const asset = getAsset(projectId, assetId);
    if (x != undefined && y != undefined) {
        asset.bounds.x = x;
        asset.bounds.y = y;
    }
});

mutator(
    patchAssetPayload,
    ({ projectId, assetId, payload, newTaskId, isPartialPayload, currentPayload, payloadId }) => {
        const asset = getAssetOrUndefined(projectId, assetId);

        if (asset) {
            asset.payload = isPartialPayload
                ? { ...(currentPayload as any), ...(payload as any) }
                : payload;
            asset.payloadId = payloadId;

            newTaskId &&
                Object.keys(newTaskId).forEach(key => {
                    asset[key] = newTaskId[key];
                });
        }
    }
);

mutator(trackAssetSize, ({ projectId, assetId, width, height }) => {
    const asset = getAsset(projectId, assetId);
    if (asset) {
        asset.bounds.lastKnownWidth = width;
        asset.bounds.lastKnownHeight = height;
    }
});

mutator(resetIsNewlyCreated, ({ projectId, assetId }) => {
    const asset = getAsset(projectId, assetId);
    if (asset) {
        asset.isNewlyCreated = false;
    }
});

mutator(removeBucketChild, ({ projectId, assetId, dragIntoBucket }) => {
    const { assets } = getProject(projectId);
    const childAsset = getAsset(projectId, assetId);
    assets.forEach(asset => {
        if (asset.type === 'bucket') {
            const index = asset.children?.indexOf(assetId) ?? -1;
            const secondIndex = asset.children?.indexOf(assetId, index + 1) ?? -1;
            // if dragged into bucket, only remove if there are two instances (reordering case) or
            // if the asset's parentId doesn't match (dragged from one bucket to another)
            if (
                index >= 0 &&
                (!dragIntoBucket ||
                    secondIndex >= 0 ||
                    (asset.outlookSpaceAssetId !== childAsset?.parentId &&
                        asset.outlookSpaceAssetId &&
                        getLocalAssetId(asset.outlookSpaceAssetId) !== childAsset?.parentId))
            ) {
                asset.children?.splice(index, 1);
            }
        }
    });
});

mutator(setDueDate, ({ projectId, assetId, dueDate, shouldRemoveTask }) => {
    const asset = getAsset(projectId, assetId);
    if (asset) {
        if (!!dueDate) {
            asset.dueDate = dueDate;
        } else {
            asset.dueDate = undefined;
            asset.isComplete = false;
            if (shouldRemoveTask) {
                asset.taskId = '';
            }
        }
    }
});

mutator(setIsComplete, ({ projectId, assetId, isComplete }) => {
    const asset = getAsset(projectId, assetId);
    if (asset) {
        asset.isComplete = isComplete;
    }
});

mutator(setTaskId, ({ projectId, assetId, taskId, taskDueDate }) => {
    const asset = getAsset(projectId, assetId);
    if (asset) {
        asset.taskId = taskId || undefined;
        if (!!taskDueDate) {
            asset.dueDate = taskDueDate;
        }
    }
});

mutator(setColor, ({ projectId, assetId, color }) => {
    const asset = getAsset(projectId, assetId);
    if (asset) {
        asset.color = color;
    }
});

mutator(upsertClientAssets, ({ projectId, outlookSpaceAssets }) => {
    const { assets } = getProject(projectId);
    upsertAssets(assets, outlookSpaceAssets, false);
});

function moveAsset(projectId: ProjectId, assetId: AssetId, x: number, y: number) {
    const { renderingOrder } = getProject(projectId);
    const asset = getAsset(projectId, assetId)!;

    asset.bounds.x = x;
    asset.bounds.y = y;

    // if the asset doesn't already exist in rendering order, add it
    if (!renderingOrder.includes(assetId)) {
        renderingOrder.push(assetId);
        asset.isCaptured = false;
    }
}
